import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  FNCO_PRICE_DISCOUNT_TYPE,
  FNCO_PRICE_SALE_TYPE,
  FNCO_PRICE_SUPPLY_TYPE,
  FNCO_PRICE_TITLE,
  type FncoPriceSupplyItem,
  type FncoPriceType,
} from '@/utils/approval/fncoPriceSupply';
import { FncoPriceSupplyRoot } from './styles';

type Column = {
  key: keyof FncoPriceSupplyItem;
  label: string;
  width?: number;
  right?: boolean;
  context?: string;
  /** 레거시는 값이 비어도 붙임 */
  suffix?: string;
};

/** 열 폭·머리글·필드 순서는 레거시 그대로. 폭이 없으면 남는 폭 */
const COLUMNS: Record<FncoPriceType, Column[]> = {
  [FNCO_PRICE_SUPPLY_TYPE]: [
    { key: 'KUNNR', label: '거래처코드', width: 120 },
    { key: 'NAME1', label: '코드명', width: 224 },
    { key: 'MATNR', label: '자재코드', width: 134 },
    { key: 'MAKTX', label: '자재코드명', width: 328 },
    { key: 'KBETR_P', label: '판매가(국내)', width: 110 },
    { key: 'KBETR_S', label: '판매가(해외)', width: 110 },
    { key: 'KONWA_S', label: '판매통화', width: 90 },
    {
      key: 'KBETR',
      label: '공급가',
      width: 150,
      right: true,
      context: 'price',
    },
    { key: 'KONWA', label: '공급통화', width: 70 },
  ],
  [FNCO_PRICE_DISCOUNT_TYPE]: [
    { key: 'KUNNR', label: '거래처코드', width: 96 },
    { key: 'NAME1', label: '코드명' },
    { key: 'MATNR', label: '자재코드', width: 134 },
    { key: 'MAKTX', label: '자재코드명' },
    { key: 'KBETR', label: '판매가', width: 74, right: true },
    { key: 'KBETR2', label: '할인율', width: 74, right: true, suffix: '%' },
  ],
  [FNCO_PRICE_SALE_TYPE]: [
    { key: 'MATNR', label: '자재코드', width: 134 },
    { key: 'MAKTX', label: '자재코드명' },
    { key: 'KBETR', label: '판매가', width: 150, right: true },
  ],
};

/** 표 아래 안내 — 레거시 문서와 같은 class 라 조회 스타일을 같이 씀 */
const NOTE: Partial<Record<FncoPriceType, string>> = {
  [FNCO_PRICE_DISCOUNT_TYPE]: '할인율: (1-출고율)',
};

// '공급가' 공용 키는 영문이 Net Amount 라 가격 등록 머리글은 context 키로 따로 번역
const columnLabel = (column: Column, t: TFunction) =>
  column.context
    ? t(column.label, { context: column.context })
    : t(column.label);

type ContentProps = {
  apvType: FncoPriceType;
  items: FncoPriceSupplyItem[];
  t: TFunction;
  /** 작성 화면은 「펼치기」 아래에 {@link FncoPriceSupplyNote} 로 따로 */
  hideNote?: boolean;
};

const NoteText = ({ note, t }: { note: string; t: TFunction }) => (
  <p className="mt-5 mb-20">
    <small className="text-muted">{t(note, { nsSeparator: false })}</small>
  </p>
);

const FncoPriceSupplyContent = ({
  apvType,
  items,
  t,
  hideNote,
}: ContentProps) => {
  const columns = COLUMNS[apvType];
  const note = hideNote ? undefined : NOTE[apvType];
  return (
    <>
      <label className="label">{t(FNCO_PRICE_TITLE[apvType])}</label>
      <table>
        <colgroup>
          {columns.map((column) => (
            <col
              key={column.key}
              width={column.width ? `${column.width}px` : undefined}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.right ? 'text-right' : undefined}
              >
                {columnLabel(column, t)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.right ? 'text-right' : undefined}
                >
                  {item[column.key] ?? ''}
                  {column.suffix}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {note && <NoteText note={note} t={t} />}
    </>
  );
};

export const FncoPriceSupplyNote = ({
  apvType,
  t,
}: {
  apvType: FncoPriceType;
  t: TFunction;
}) => {
  const note = NOTE[apvType];
  if (!note) return null;
  return (
    <FncoPriceSupplyRoot>
      <NoteText note={note} t={t} />
    </FncoPriceSupplyRoot>
  );
};

/** attributes.content — 조회 화면은 이 HTML 을 그대로 보여 줌 */
export const buildFncoPriceSupplyContentHtml = (props: ContentProps): string =>
  renderToStaticMarkup(<FncoPriceSupplyContent {...props} />);

// 레거시 문서 content 에는 숨긴 「펼치기」 버튼 줄이 함께 저장돼 있음
const DROP_TAGS = new Set(['style', 'script', 'link', 'input', 'button']);
const DROP_IDS = new Set(['toggleButtonRow']);
const TABLE_PARTS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'colgroup',
]);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

type SnapshotProps = {
  content: string;
  /** 작성 화면은 필드 머리글이 따로 있어 저장 HTML 의 제목을 숨김 */
  hideTitle?: boolean;
};

/** 저장 content(레거시 문서 포함) 그대로 — 표만 공통 양식 표로 */
export const FncoPriceSupplySnapshot = ({
  content,
  hideTitle,
}: SnapshotProps) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (DROP_IDS.has(element.id)) return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      const key = props.key as string;
      if (tagName === 'label') {
        if (hideTitle) return null;
        return (
          <InfoSectionTitle key={key} sx={{ mb: 1 }}>
            {children}
          </InfoSectionTitle>
        );
      }
      if (tagName === 'table') {
        return (
          <ApprovalFormTable key={key} className={props.className as string}>
            {children}
          </ApprovalFormTable>
        );
      }
      return undefined;
    },
    [hideTitle],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <FncoPriceSupplyRoot>{rendered}</FncoPriceSupplyRoot>;
};
